import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, ArrowRight, Check, X, Gamepad2, Calculator,
  Package, Gift, ChevronDown
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TradeInPage = () => {
  const { theme } = useTheme();
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetails, setProductDetails] = useState(null);
  const [condition, setCondition] = useState('good');
  const [extras, setExtras] = useState({
    with_controller: false,
    with_game: false,
    with_box: false,
    with_dock: false
  });
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/tradein/products`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const fetchProductDetails = async (productId) => {
    try {
      const response = await axios.get(`${API_URL}/api/tradein/${productId}`);
      setProductDetails(response.data);
      setSelectedProduct(productId);
      setStep(2);
    } catch (error) {
      console.error('Error fetching product details:', error);
    }
  };

  const calculateEstimate = async () => {
    try {
      const params = new URLSearchParams({
        product_id: selectedProduct,
        condition,
        ...extras
      });
      const response = await axios.post(`${API_URL}/api/tradein/estimate?${params}`);
      setEstimate(response.data);
      setStep(3);
    } catch (error) {
      console.error('Error calculating estimate:', error);
    }
  };

  const conditions = [
    { id: 'excellent', label: 'Excelente', description: 'Como nuevo, sin arañazos' },
    { id: 'good', label: 'Bueno', description: 'Buen estado, uso normal' },
    { id: 'fair', label: 'Regular', description: 'Arañazos visibles, funciona' },
    { id: 'poor', label: 'Dañado', description: 'Daños estéticos/funcionales' }
  ];

  const getConditionColor = (cond) => {
    switch (cond) {
      case 'excellent': return 'border-[#10B981] bg-[#10B981]/10 text-[#10B981]';
      case 'good': return 'border-[#0047FF] bg-[#0047FF]/10 text-[#0047FF]';
      case 'fair': return 'border-[#F59E0B] bg-[#F59E0B]/10 text-[#F59E0B]';
      case 'poor': return 'border-[#EF4444] bg-[#EF4444]/10 text-[#EF4444]';
      default: return '';
    }
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-[#F5F5F7]'}`}>
      <header className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-[#E5E7EB]'} border-b`}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link
            to="/dashboard"
            className="flex items-center gap-2 text-[#52525B] dark:text-gray-400 hover:text-[#0A0A0A] dark:hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#0047FF]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Calculator className="w-8 h-8 text-[#0047FF]" />
            </div>
            <h1 className={`text-3xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : ''}`}>
              Programa Trade-In
            </h1>
            <p className="text-[#52525B] dark:text-gray-400">
              Envía tu consola vieja y recibe crédito para una nueva
            </p>
          </div>

          {/* Progress steps */}
          <div className="flex items-center justify-center gap-4 mb-8">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= s ? 'bg-[#0047FF] text-white' : theme === 'dark' ? 'bg-gray-700 text-gray-400' : 'bg-[#E5E7EB] text-[#A1A1AA]'
                }`}>
                  {step > s ? <Check className="w-5 h-5" /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-16 h-1 rounded ${step > s ? 'bg-[#0047FF]' : theme === 'dark' ? 'bg-gray-700' : 'bg-[#E5E7EB]'}`} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Step 1: Select product */}
          {step === 1 && (
            <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-[#E5E7EB]'} rounded-xl border p-6`}>
              <h2 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : ''}`}>
                1. Selecciona tu producto
              </h2>
              
              {loading ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-2 border-[#0047FF] border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  {products.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => fetchProductDetails(product.id)}
                      className={`p-4 rounded-xl border text-left transition-all hover:-translate-y-1 ${
                        theme === 'dark' 
                          ? 'bg-gray-700 border-gray-600 hover:border-[#0047FF]' 
                          : 'bg-[#F5F5F7] border-[#E5E7EB] hover:border-[#0047FF]'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          product.name.toLowerCase().includes('playstation') ? 'bg-[#003087]/10' :
                          product.name.toLowerCase().includes('nintendo') ? 'bg-[#E60012]/10' :
                          product.name.toLowerCase().includes('xbox') ? 'bg-[#107C10]/10' : 'bg-[#0047FF]/10'
                        }`}>
                          <Gamepad2 className={`w-6 h-6 ${
                            product.name.toLowerCase().includes('playstation') ? 'text-[#003087]' :
                            product.name.toLowerCase().includes('nintendo') ? 'text-[#E60012]' :
                            product.name.toLowerCase().includes('xbox') ? 'text-[#107C10]' : 'text-[#0047FF]'
                          }`} />
                        </div>
                        <div>
                          <p className={`font-medium ${theme === 'dark' ? 'text-white' : ''}`}>
                            {product.name}
                          </p>
                          <p className="text-sm text-[#10B981]">Hasta {product.max_value}€</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Condition and extras */}
          {step === 2 && productDetails && (
            <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-[#E5E7EB]'} rounded-xl border p-6`}>
              <h2 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : ''}`}>
                2. Estado del {productDetails.name}
              </h2>
              
              <div className="space-y-4">
                {/* Condition selector */}
                <div className="grid sm:grid-cols-2 gap-3">
                  {conditions.map((cond) => (
                    <button
                      key={cond.id}
                      onClick={() => setCondition(cond.id)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        condition === cond.id 
                          ? getConditionColor(cond.id)
                          : theme === 'dark' ? 'border-gray-600' : 'border-[#E5E7EB]'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`font-medium ${theme === 'dark' && condition !== cond.id ? 'text-white' : ''}`}>
                            {cond.label}
                          </p>
                          <p className="text-sm text-[#52525B] dark:text-gray-400">{cond.description}</p>
                        </div>
                        {productDetails.conditions[cond.id] && (
                          <span className="text-lg font-bold text-[#0047FF]">
                            {productDetails.conditions[cond.id].value}€
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Extras */}
                <div className="mt-6">
                  <p className={`font-medium mb-3 ${theme === 'dark' ? 'text-white' : ''}`}>
                    ¿Incluyes extras? (bonificación adicional)
                  </p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {[
                      { key: 'with_controller', label: 'Mando incluido', bonus: productDetails.bonus?.with_controller },
                      { key: 'with_game', label: 'Juego incluido', bonus: productDetails.bonus?.with_game },
                      { key: 'with_box', label: 'Caja original', bonus: productDetails.bonus?.with_box },
                      { key: 'with_dock', label: 'Dock/Base', bonus: productDetails.bonus?.with_dock }
                    ].filter(e => e.bonus > 0).map((extra) => (
                      <button
                        key={extra.key}
                        onClick={() => setExtras(prev => ({ ...prev, [extra.key]: !prev[extra.key] }))}
                        className={`p-3 rounded-lg border flex items-center justify-between ${
                          extras[extra.key]
                            ? 'border-[#0047FF] bg-[#0047FF]/10'
                            : theme === 'dark' ? 'border-gray-600' : 'border-[#E5E7EB]'
                        }`}
                      >
                        <span className={theme === 'dark' ? 'text-white' : ''}>{extra.label}</span>
                        <span className="text-[#10B981]">+{extra.bonus}€</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <Button variant="outline" onClick={() => setStep(1)}>
                    Atrás
                  </Button>
                  <Button onClick={calculateEstimate} className="flex-1 bg-[#0047FF] hover:bg-[#003BCC]">
                    Calcular valor
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Estimate result */}
          {step === 3 && estimate && (
            <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-[#E5E7EB]'} rounded-xl border p-6`}>
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-[#10B981]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Gift className="w-10 h-10 text-[#10B981]" />
                </div>
                <h2 className={`text-2xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : ''}`}>
                  ¡Tu {estimate.product} vale hasta
                </h2>
                <p className="text-5xl font-bold text-[#10B981]">{estimate.total_value}€</p>
              </div>

              <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-[#F5F5F7]'} mb-6`}>
                <div className="flex justify-between mb-2">
                  <span className="text-[#52525B] dark:text-gray-400">Valor base ({estimate.condition})</span>
                  <span className={theme === 'dark' ? 'text-white' : ''}>{estimate.base_value}€</span>
                </div>
                {estimate.bonus > 0 && (
                  <div className="flex justify-between mb-2">
                    <span className="text-[#52525B] dark:text-gray-400">Bonus extras</span>
                    <span className="text-[#10B981]">+{estimate.bonus}€</span>
                  </div>
                )}
                <div className="border-t border-[#E5E7EB] dark:border-gray-600 pt-2 mt-2">
                  <div className="flex justify-between font-bold">
                    <span className={theme === 'dark' ? 'text-white' : ''}>Total</span>
                    <span className="text-[#10B981]">{estimate.total_value}€</span>
                  </div>
                </div>
              </div>

              <p className="text-sm text-[#52525B] dark:text-gray-400 text-center mb-6">
                Este valor es una estimación. El precio final se determinará tras inspeccionar el producto.
              </p>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(2)}>
                  Recalcular
                </Button>
                <Link to="/warranty/new" className="flex-1">
                  <Button className="w-full bg-[#0047FF] hover:bg-[#003BCC]">
                    Iniciar Trade-In
                    <Package className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default TradeInPage;
